import React from "react";
import RegularButton from "components/CustomButtons/Button";
import { orange, blue } from "@mui/material/colors";
import { timeStatus } from "helpers/timeStatus";
import { colorExaToRgb } from "helpers/formatColor";
import SettingsIcon from "@mui/icons-material/Settings";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useDispatch } from "react-redux";
import { selectOrder, getOrderEta } from "redux/actions/orders/ordersActions";
import { getSchedules } from "redux/actions/customers/customersAction";
import { getCoupons } from "redux/actions/coupons/couponsActions";
import { useToggle } from "hooks/useToggle";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";
import { originLabelMap } from "helpers/dictionary";

import { ModalV2 } from "components/Modal/ModalV2";
import { IssueForm } from "../Issues/IssueForm";
import { SubscriptionApprovalModal } from "./SubscriptionApprovalModal";
import { ORDERS_LABELS } from "./ordersLabels";
import { FullTankLabel } from "./FullTankLabel";

const { headers, tooltips, modals } = ORDERS_LABELS;
const colorValue = 500;
export const columns = [
  {
    Header: headers.customer,
    accessor: "customer",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.customer).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.account,
    accessor: "account",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.account).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.phone,
    accessor: "phone",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.phone).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.type,
    accessor: "type",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.type).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.folio,
    accessor: "folio",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.folio).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.amount,
    accessor: "total",
    filterable: true,
    filterMethod: (filter, row) => String(row.total).includes(filter.value),
    Cell: ({ original }) =>
      original?.details?.[0]?.quantity === 0 && !original?.delivery ? (
        <FullTankLabel />
      ) : original.total,
  },
  {
    Header: headers.createdDate,
    accessor: "created",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.created).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.scheduledDate,
    accessor: "date",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.date).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.status,
    accessor: "nStatus",
    filterable: true,
    filterMethod: (filter, row) => {
      return String(row.nStatus).toLowerCase().includes(filter.value.toLowerCase());
    },
    Cell: ({ original }) => {
      const { nStatus, colorStatus } = original;
      return (
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            color: colorStatus,
          }}
        >
          {nStatus}
        </div>
      );
    },
  },
  {
    Header: headers.station,
    accessor: "station",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.station).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: "Marca",
    accessor: "brand",
    filterable: true,
    filterMethod: (filter, row) =>
      row.brand?.toLowerCase()?.includes(filter.value?.toLowerCase()),
  },
  {
    Header: "Origen",
    id: "origin",
    accessor: (a) => a?.serviceComplement?.origin,
    filterable: true,
    filterMethod: (filter, row) =>
      row.origin?.toLowerCase()?.includes(filter.value?.toLowerCase()),
    Cell: ({ original }) => {
      const rawOrigin = original?.serviceComplement?.origin;
      const originLabel = rawOrigin ? (originLabelMap[rawOrigin] || "Otro") : "N/A";
      return (
        <div style={{ textAlign: "center" }}>
          {originLabel}
        </div>
      );
    },
  },
  {
    Header: headers.unit,
    accessor: "unit",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.unit).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.agent,
    accessor: "user",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.user).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.address,
    accessor: "addressComplete",
    filterable: true,
    filterMethod: (filter, row) =>
      String(row.addressComplete).toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: headers.actions,
    getProps: (state, rowInfo) => {
      if (!rowInfo) return {};
      const backgroundColor = rowInfo.original.id
        ? `linear-gradient(94deg, rgba(255,255,255,0) 20%, ${colorExaToRgb(
          timeStatus[rowInfo.original.timing].color,
          0.3,
        )} 100%)`
        : "";

      return {
        style: {
          background: backgroundColor,
        },
      };
    },
    Cell: ({ original }) => {
      const hist = useHistory();
      const dispatch = useDispatch();
      const canApproveSubscription = useRuleValidator(PERMISSIONS.ORDERS_SUBSCRIPTION_APPROVE);
      const [openIssueForm, setOpenIssueForm] = useToggle();
      const [openApprovalModal, setOpenApprovalModal] = useToggle();
      
      const handleNav = () => {
        dispatch(selectOrder(original));
        dispatch(getCoupons(original?.address.id));
        dispatch(getSchedules(original?.idStation, null, true));
        dispatch(getOrderEta([{ id: original.id }]));
        hist.push(`/admin/seguimiento_pedido_orden/${original.id}`);
      };

      const isSubscriptionWithoutPayment = 
        original?.serviceComplement?.origin === 'SUBSCRIPTION' && 
        !original?.serviceComplement?.subscriptionApproved &&
        original?.status !== 'DONE' &&
        original?.status !== 'CANCELED';

      return (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 4,
            }}
          >
            {original?.coupon?.id ? (
              <RegularButton
                justIcon
                round
                size="sm"
                color="transparent"
                disabled
                tooltip={tooltips.activeCoupon}
              >
                <ConfirmationNumberIcon
                  style={{
                    color: orange[300],
                    margin: 0,
                  }}
                />
              </RegularButton>
            ) : null}
            {canApproveSubscription && isSubscriptionWithoutPayment && (
              <RegularButton
                justIcon
                round
                size="sm"
                color="transparent"
                tooltip="Aprobar/Rechazar pedido"
                onClick={setOpenApprovalModal}
              >
                <CheckCircleIcon
                  style={{
                    color: blue[colorValue],
                    margin: 0,
                  }}
                />
              </RegularButton>
            )}
            <RegularButton
              justIcon
              round
              size="sm"
              color="transparent"
              tooltip={tooltips.details}
              onClick={handleNav}
            >
              <SettingsIcon
                style={{
                  color: original?.annotations
                    ? orange[colorValue]
                    : blue[colorValue],
                }}
              />
            </RegularButton>
            <RegularButton
              color="transparent"
              round
              justIcon
              onClick={setOpenIssueForm}
              tooltip={tooltips.issueTicket}
            >
              <ReportProblemIcon
                style={{ margin: 0, color: orange[colorValue] }}
              />
            </RegularButton>
          </div>

          <ModalV2
            open={openIssueForm}
            onClose={setOpenIssueForm}
            title={modals.createTicket}
            toRight
          >
            <IssueForm
              onClose={setOpenIssueForm}
              isNew
              account={{
                ...original,
                code: original.account,
                idAccount: original.idAccount,
                observations: null,
                id: null,
                status: "ACTIVE",
              }}
            />
          </ModalV2>

          <SubscriptionApprovalModal
            open={openApprovalModal}
            onClose={setOpenApprovalModal}
            order={original}
            onSuccess={() => {
              // Recargar datos si es necesario
            }}
          />
        </>
      );
    },
  },
];
